import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import TemplateFieldsForm from '../../../components/TemplateFieldsForm';
import { ITemplate } from '../../../types/damTypes';
import { LibraryIntent, PresentationAssetDetail as Detail } from '../types';
import { PRES } from '../chrome';
import { COPY } from '../presentationCopy';
import { LibraryCategory, matchLibraryTemplate } from '../libraryCatalog';
import { resolveMediaUrl } from './slidePreview';
import { toLibraryFieldTransfer, writeLibraryFieldTransfer } from './libraryFieldTransfer';

interface PresentationAssetDetailProps {
  asset: Detail;
  categories: LibraryCategory[];
  templates: ITemplate[];
  intent: LibraryIntent;
  onClose: () => void;
  onPlace: (asset: Detail, intent: LibraryIntent) => void;
}

const PresentationAssetDetail: React.FC<PresentationAssetDetailProps> = ({
  asset,
  categories,
  templates,
  intent,
  onClose,
  onPlace,
}) => {
  const placeIntent: LibraryIntent = intent === 'insert-image' && asset.placeKind === 'product'
    ? 'insert-product'
    : intent;
  const placeLabel = placeIntent === 'replace'
    ? COPY.replaceMedia
    : placeIntent === 'insert-product'
      ? COPY.placeAsProduct
      : COPY.placeAsImage;
  const template = useMemo(
    () => matchLibraryTemplate(asset.categories.map((category) => category._id), templates, categories),
    [asset.categories, templates, categories],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', color: PRES.interactive, minWidth: 0, px: 0.5 }}>
          ← {COPY.library}
        </Button>
      </Box>
      <Box sx={{ px: 1.5, pb: 2, overflow: 'auto', flex: 1 }}>
        <Box sx={{ height: 180, bgcolor: PRES.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, mb: 1.5 }}>
          {asset.previewUrl ? (
            <Box component="img" src={resolveMediaUrl(asset.previewUrl)} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <Typography variant="caption" color="text.secondary">{asset.fileType || asset.fileName}</Typography>
          )}
        </Box>
        {!template && (
          <>
            <Typography sx={{ color: PRES.textPrimary, fontWeight: 700, fontSize: 15 }}>{asset.title || asset.fileName}</Typography>
            {asset.product?.articleNumber && (
              <Typography variant="body2" sx={{ mt: 1 }}>Artikelnummer: {asset.product.articleNumber}</Typography>
            )}
          </>
        )}
        <Typography variant="body2" sx={{ color: PRES.muted, mt: template ? 0 : 0.5 }}>{asset.fileName}</Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: PRES.muted }}>
          {asset.fileType || 'fil'} · {asset.placeKind === 'product' ? COPY.product : COPY.image}
        </Typography>
        {!!asset.categories.length && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {asset.categories.map((category) => category.name).filter(Boolean).join(', ')}
          </Typography>
        )}
        {!!asset.tags.length && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: PRES.muted }}>
            {asset.tags.join(', ')}
          </Typography>
        )}
        <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: PRES.muted }}>
          Version: {asset.currentVersionId?.slice(-8) || 'aktuell'}
        </Typography>
        {template && (
          <Box sx={{ mt: 1.5, userSelect: 'text' }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: PRES.muted }}>
              {COPY.fieldTextHint}
            </Typography>
            <TemplateFieldsForm
              template={template}
              values={asset.metadata || {}}
              onChange={() => undefined}
              disabled
              showUploadFields={false}
              showEditFields
              groupBySeparators
              onValueDragStart={(event, payload) => {
                const transfer = toLibraryFieldTransfer(payload);
                if (!transfer) {
                  event.preventDefault();
                  return;
                }
                writeLibraryFieldTransfer(event.dataTransfer, transfer);
              }}
            />
          </Box>
        )}
        <Button
          fullWidth
          variant="contained"
          disabled={!asset.placeable}
          onClick={() => onPlace(asset, placeIntent)}
          sx={{ mt: 2, bgcolor: PRES.interactive, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: PRES.interactiveHover } }}
        >
          {placeLabel}
        </Button>
        {!asset.placeable && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#8a5a00' }}>
            {asset.unsupportedReason || COPY.unsupportedPlace}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PresentationAssetDetail;
